"use client";

import { type DefaultLinkProps, Link } from "@pamperedchef/design-system";
import * as styles from "./MinimalFooter.module.scss";

const PRIVACY_HREF = "/about-us-legal-privacy-page";

export interface MinimalFooterProps {
	/** Defaults to the current calendar year. */
	year?: number;
}

/**
 * Minimal page footer — two legal links + copyright on a light wash band.
 *
 * Used by landing pages whose `basic_page.page_layout = "Minimal"`. Links
 * and copy are intentionally hardcoded; this is a thin legal-only shell,
 * not a full sitemap. If the design grows, promote to a CMS-driven shell
 * (the `public_template` POC is the seed for that).
 *
 * `Cookie Preferences` opens the Osano consent drawer when present;
 * otherwise it falls through silently (TODO: wire the real Osano API once
 * the integration shape is finalized).
 */
export function MinimalFooter({
	year = new Date().getFullYear()
}: MinimalFooterProps): JSX.Element {
	return (
		<footer className={styles.footer} data-component="MinimalFooter">
			<nav className={styles["footer__links"]} aria-label="Legal">
				<Link<DefaultLinkProps>
					href={PRIVACY_HREF}
					title="Privacy Policy"
					className={styles["footer__link"]}
				>
					Privacy Policy
				</Link>
				<button
					type="button"
					className={styles["footer__cookie-button"]}
					onClick={() => {
						if (
							typeof window !== "undefined" &&
							(
								window as unknown as {
									Osano?: {
										cm?: {
											showDrawer?: (id: string) => void;
										};
									};
								}
							).Osano?.cm?.showDrawer
						) {
							(
								window as unknown as {
									Osano: {
										cm: {
											showDrawer: (id: string) => void;
										};
									};
								}
							).Osano.cm.showDrawer(
								"osano-cm-dom-info-dialog-open"
							);
						}
					}}
				>
					Cookie Preferences
				</button>
			</nav>
			<p className={styles["footer__copyright"]}>
				© {year} Pampered Chef used under license. All Rights Reserved.
			</p>
		</footer>
	);
}
